import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, StarOff } from "lucide-react";
import {
  LayoutDashboard, ListPlus, ArrowLeftRight, Banknote, ScrollText,
  Users, FileText, Store, Receipt, Repeat, Filter, UsersRound,
  Package, Boxes, BookOpen, BarChart3, LineChart, TrendingUp,
  Sparkles, Bell, Bookmark, Settings, ShieldCheck, Wallet, Building2,
  DollarSign,
} from "lucide-react";

const FAV_KEY = "accountrix.hub.favourites";
const MAX_FAVS = 3;

// Curated module grid — grouped by workflow, arranged for QuickBooks-style
// muscle memory: Money In (top), Money Out, Banking, Analyze, Team.
const GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", icon: LayoutDashboard, name: "Dashboard", desc: "Cash, KPIs, alerts", accent: "bg-[#2F4F4F] text-white" },
      { to: "/executive", icon: TrendingUp,      name: "Executive KPIs", desc: "Board-level view", accent: "bg-[#F5EAE3] text-[#E07A5F]" },
      { to: "/analytics", icon: LineChart,       name: "Analytics", desc: "Trends & breakdowns", accent: "bg-[#EDF3EF] text-[#4C956C]" },
      { to: "/ai",        icon: Sparkles,        name: "AI Advisor", desc: "Ask your books anything", accent: "bg-[#FFF9F5] text-[#E07A5F]" },
    ],
  },
  {
    label: "Money In",
    items: [
      { to: "/receivables", icon: Users,      name: "Customers & Invoices", desc: "Bill customers, chase payments", accent: "bg-[#E8F1EE] text-[#2F4F4F]" },
      { to: "/receive-payment",    icon: DollarSign, name: "Receive Payment",       desc: "Log payments across invoices",   accent: "bg-[#EDF3EF] text-[#4C956C]" },
      { to: "/customer-memos",     icon: ScrollText, name: "AR Memos",              desc: "Credit & debit memos",           accent: "bg-[#EDF3EF] text-[#4C956C]" },
      { to: "/recurring",          icon: Repeat,     name: "Recurring Invoices",    desc: "Auto-bill on schedule",          accent: "bg-[#F5EAE3] text-[#E07A5F]" },
    ],
  },
  {
    label: "Money Out",
    items: [
      { to: "/payables",  icon: Store,      name: "Vendors & Bills", desc: "Track what you owe",     accent: "bg-[#FBEEE7] text-[#B4283A]" },
      { to: "/pay-bills",      icon: DollarSign, name: "Pay Bills",       desc: "Log payments across bills",  accent: "bg-[#FBEEE7] text-[#B4283A]" },
      { to: "/vendor-memos",   icon: ScrollText, name: "AP Memos",        desc: "Credit & debit notes",   accent: "bg-[#EDF3EF] text-[#4C956C]" },
      { to: "/transactions",   icon: ListPlus,   name: "Transactions",    desc: "All ins & outs",         accent: "bg-[#F0F0F0] text-[#2F4F4F]" },
      { to: "/rules",          icon: Filter,     name: "Rules Engine",    desc: "Auto-categorise",        accent: "bg-[#F5EAE3] text-[#E07A5F]" },
    ],
  },
  {
    label: "Banking & Money Movement",
    items: [
      { to: "/banks",     icon: Banknote,       name: "Banking",       desc: "Plaid · Mono · PayPal",  accent: "bg-[#E8F1EE] text-[#2F4F4F]" },
      { to: "/transfers", icon: ArrowLeftRight, name: "Transfers",     desc: "Move funds anywhere",    accent: "bg-[#FFF9F5] text-[#E07A5F]" },
      { to: "/financials?tab=reconciliation", icon: ShieldCheck, name: "Reconciliation", desc: "Match statements",   accent: "bg-[#F5EAE3] text-[#E07A5F]" },
      { to: "/investments", icon: LineChart,    name: "Investments",   desc: "Portfolio & yields",     accent: "bg-[#EDF3EF] text-[#4C956C]" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/payroll",  icon: UsersRound, name: "Payroll",        desc: "Employees & pay runs",   accent: "bg-[#F0F0F0] text-[#2F4F4F]" },
      { to: "/inventory", icon: Package,    name: "Products & Stock", desc: "Inventory",              accent: "bg-[#F5EAE3] text-[#E07A5F]" },
      { to: "/financials?tab=assets", icon: Boxes,  name: "Fixed Assets",   desc: "Depreciation",           accent: "bg-[#EDF3EF] text-[#4C956C]" },
    ],
  },
  {
    label: "Accounting & Reports",
    items: [
      { to: "/chart-of-accounts",       icon: BookOpen, name: "Chart of Accounts", desc: "Your CoA",           accent: "bg-[#E8F1EE] text-[#2F4F4F]" },
      { to: "/reports",   icon: BarChart3, name: "Reports",          desc: "P&L, BS, CF, GL",    accent: "bg-[#F5EAE3] text-[#E07A5F]" },
      { to: "/reports/cash-register", icon: BookOpen, name: "Cash Register", desc: "Daily cash book with running balance", accent: "bg-[#E8F1EE] text-[#2F4F4F]" },
      { to: "/reports/designer", icon: FileText, name: "Report Designer", desc: "Custom reports", accent: "bg-[#FFF9F5] text-[#E07A5F]" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/team",          icon: Users,    name: "Team",         desc: "Invite & permissions", accent: "bg-[#F0F0F0] text-[#2F4F4F]" },
      { to: "/notifications", icon: Bell,     name: "Notifications", desc: "Alerts & digests",    accent: "bg-[#FBEEE7] text-[#B4283A]" },
      { to: "/settings",      icon: Settings, name: "Settings",     desc: "Profile & prefs",     accent: "bg-[#F5EAE3] text-[#E07A5F]" },
    ],
  },
];

const slug = (to) => to.replace(/^\//, "").replace(/[/?=&]/g, "-");

export default function Hub() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      setFavs(raw ? JSON.parse(raw) : []);
    } catch { setFavs([]); }
  }, []);

  const toggleFav = (to) => {
    setFavs((cur) => {
      let next;
      if (cur.includes(to)) {
        next = cur.filter((x) => x !== to);
      } else {
        next = [to, ...cur].slice(0, MAX_FAVS);
      }
      try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/summary");
        setStats(data);
      } catch (err) { /* silent */ }
    })();
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <AppShell>
      <div className="mb-10" data-testid="hub-header">
        <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
          Your workspace
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">
          {greeting}, {firstName}.
        </h1>
        <p className="text-sm text-[var(--muted-text)] mt-2 max-w-xl">
          Every module of Accountrix is one tap away. Pick where you want to work — your books will follow.
        </p>
      </div>

      {/* Quick snapshot */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="hub-snapshot">
          {[
            { label: "Income", value: stats.totals?.income, tone: "text-[#4C956C]" },
            { label: "Expenses", value: stats.totals?.expense, tone: "text-[#B4283A]" },
            { label: "Savings", value: stats.totals?.saving, tone: "text-[#E07A5F]" },
            { label: "Net", value: (stats.totals?.income || 0) - (stats.totals?.expense || 0), tone: "text-[#2F4F4F]" },
          ].map((s) => (
            <div key={s.label} className="card-flat p-4">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                {s.label}
              </div>
              <div className={`font-display text-2xl mt-1 font-mono-data ${s.tone}`}>
                ${(s.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Favourites row */}
      {favs.length > 0 && (
        <div className="mb-8" data-testid="hub-favourites">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-4 h-4 text-[#E07A5F] fill-[#E07A5F]" />
            <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
              Your favourites
            </div>
            <div className="flex-1 h-px bg-[var(--divider)]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {favs.map((to) => {
              const item = GROUPS.flatMap((g) => g.items).find((x) => x.to === to);
              if (!item) return null;
              const Icon = item.icon;
              return (
                <button
                  key={to}
                  data-testid={`hub-fav-${slug(to)}`}
                  onClick={() => nav(to)}
                  className="group card-flat p-4 text-left ring-2 ring-[#E07A5F]/30 hover:ring-[#E07A5F] transition"
                >
                  <div className={`w-10 h-10 rounded-lg ${item.accent} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-lg mt-3">{item.name}</div>
                  <div className="text-xs text-[var(--muted-text)] mt-1 leading-snug">{item.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Module groups */}
      <div className="space-y-8" data-testid="hub-modules">
        {GROUPS.map((g) => (
          <div key={g.label}>
            <div className="flex items-center gap-2 mb-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
                {g.label}
              </div>
              <div className="flex-1 h-px bg-[var(--divider)]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {g.items.map((m) => {
                const Icon = m.icon;
                const isFav = favs.includes(m.to);
                return (
                  <div
                    key={m.to}
                    className="relative group"
                  >
                    <button
                      data-testid={`hub-tile-${slug(m.to)}`}
                      onClick={() => nav(m.to)}
                      className="w-full card-flat p-4 text-left hover:border-[var(--brand)] hover:-translate-y-0.5 transition"
                    >
                      <div className={`w-10 h-10 rounded-lg ${m.accent} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="font-display text-lg mt-3">{m.name}</div>
                      <div className="text-xs text-[var(--muted-text)] mt-1 leading-snug">
                        {m.desc}
                      </div>
                    </button>
                    <button
                      data-testid={`hub-fav-toggle-${slug(m.to)}`}
                      onClick={(e) => { e.stopPropagation(); toggleFav(m.to); }}
                      className="absolute top-2 right-2 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-[var(--hover)] transition"
                      title={isFav ? "Remove from favourites" : "Pin to favourites"}
                    >
                      {isFav
                        ? <Star className="w-4 h-4 text-[#E07A5F] fill-[#E07A5F]" />
                        : <StarOff className="w-4 h-4 text-[var(--muted-text)]" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
